import React, { useState, useEffect } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Button,
    IconButton,
    InputAdornment,
    styled,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    Chip
} from '@mui/material';

import { useNavigate, useParams } from "react-router-dom";
import { useUsers, userTypes, targetTypes } from "../../../hooks/api/useUsers";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useAppContextFeatures } from "../../../context/AppContext";
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";
import { useTranslations } from '../../../hooks/useTranslations';

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonRestricted from "../../common/CommonRestricted";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 800
}))

const StyledContent = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem'
}))

const StyledContent2 = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    mt: 2
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
    mt: 1,
    mb: 0,
    borderStyle: 'dashed'
}))

export default function ViewSupplier({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@user"] })

    const { id } = useParams();

    const { setSystemLoading } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { get: getUser } = useUsers({ userType: userTypes.supplier, targetType: targetTypes.supplier });
    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.supplier.id)

    const navigate = useNavigate();
    const [data, setData] = useState()

    const fetchData = async () => {

        setSystemLoading(true);

        try {

            let response = await getUser({ id });

            console.log('supplier', response?.data)

            setData(response?.data)

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

    }

    useEffect(() => {

        fetchData();

    }, [])

    const getBreadcumbActionMenus = () => {

        let props = []

        if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id })) {
            props.push({
                displayText: t("editProfile"),
                href: getRoute(`users/supplier/update/${id}`)
            })
        }

        return props

    }

    const RenderFormProfile = () => {

        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={6} sm={6} md={6} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`profile`)}
                            subTitle={t(`supplierProfileDetails`)}
                        />

                    </Grid>

                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('username')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {data?.supplier?.username}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('name')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {`${data?.supplier?.firstname} ${data?.supplier?.lastname}`}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('contact')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {data?.supplier?.contact}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('email')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {data?.supplier?.email}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('companyName')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {data?.supplier?.company_name}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('code')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {data?.supplier?.code}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('address')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {`${data?.supplier.address_1} ${data?.supplier.address_2 ? data?.supplier.address_2 : ""}`}
                                </StyledContent>
                                <StyledContent2>
                                    {data?.supplier?.postcode}, {data?.supplier?.area} {data?.supplier?.country_name}
                                </StyledContent2>
                            </Grid>

                        </Grid>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormBankInfo = () => {

        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={6} sm={6} md={6} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`bankInfo`)}
                            subTitle={t(`supplierBankDetails`)}
                        />

                    </Grid>

                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('bankName')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {data?.supplier?.bank_name}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                                <StyledLabel>{t('bankAccount')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={9} textAlign={"left"}>
                                <StyledContent>
                                    {data?.supplier?.bank_account}
                                </StyledContent>
                            </Grid>

                        </Grid>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {
        return (
            <>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={7} textAlign={"left"}>
                        <RenderFormProfile />
                    </Grid>
                    <Grid item xs={12} sm={12} md={5} textAlign={"left"}>
                        <RenderFormBankInfo />
                    </Grid>
                </Grid>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"center"}>
                        <Button
                            type="button"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            onClick={() => navigate(-1)}
                        >
                            {t('back')}
                        </Button>
                    </Grid>
                </Grid>
            </>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.viewSupplier })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('viewSupplier')}
                actionMenus={
                    getBreadcumbActionMenus()
                }
                breadcrumbs={[
                    {
                        displayText: t("suppliers"),
                        props: {
                            // underline: "hover",
                            href: getRoute("users/suppliers")
                        }
                    },
                    {
                        displayText: t("viewSupplier"),
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageView.id }) ?
                    (
                        <RenderForm />
                    )
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}